import React, { useState, useEffect } from 'react';
import useInView from '../hooks/useInView';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations/translations';

const BeforeAfterSection = () => {
  const [sectionRef, sectionInView] = useInView();
  const [showAfter, setShowAfter] = useState(false);
  const { language } = useLanguage();

  // Alternance toutes les 4 secondes entre "avant" et "après"
  useEffect(() => {
    const interval = setInterval(() => {
      setShowAfter((prev) => !prev);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 bg-black relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-64 h-64 bg-[#DC1A5F] rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-[#32D6E0] rounded-full filter blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Titre et description */}
        <div 
          ref={sectionRef}
          className={`text-center max-w-4xl mx-auto mb-16 transition-all duration-1000 ease-out ${
            sectionInView 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <span className="gradient-text">{getTranslation(language, 'beforeAfter.title')}</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            {getTranslation(language, 'beforeAfter.description')}
          </p>
        </div>

        {/* Container des interfaces */}
        <div className="max-w-6xl mx-auto">
          {/* Label "Avant" ou "Après" */}
          <div className="text-center mb-8">
            <span 
              className={`inline-block px-6 py-2 md:px-8 md:py-3 rounded-full text-lg md:text-xl font-bold transition-all duration-500 ${
                showAfter 
                  ? 'bg-[#DC1A5F] text-white scale-110' 
                  : 'bg-gray-800 text-gray-400 scale-100'
              }`}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {showAfter ? getTranslation(language, 'beforeAfter.after') : getTranslation(language, 'beforeAfter.before')}
            </span>
          </div>

          {/* Interfaces */}
          <div className="relative flex justify-center items-center min-h-[400px] md:min-h-[650px]">
            {/* Interfaces AVANT */}
            <div 
              className={`flex justify-center gap-8 md:gap-16 absolute inset-0 transition-all duration-700 ${
                showAfter 
                  ? 'opacity-0 scale-95 blur-sm pointer-events-none' 
                  : 'opacity-100 scale-100 blur-0'
              }`}
            >
              {/* Interface Avant 1 */}
              <div className="relative group flex-shrink-0 transform transition-all duration-500 hover:scale-105">
                <img 
                  src="/references/before-1.png" 
                  alt="Interface avant transformation 1"
                  className="w-auto h-auto object-contain shadow-2xl max-w-[179.71px] max-h-[363.46px] md:max-w-[299.52px] md:max-h-[605.76px]"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* Placeholder */}
                <div className="hidden w-[179.71px] h-[363.46px] md:w-[299.52px] md:h-[605.76px] flex-col items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl">
                  <div className="w-20 h-20 bg-gray-600 rounded-xl mb-4 flex items-center justify-center text-white text-3xl font-bold">
                    1
                  </div>
                  <p className="text-sm text-gray-400">Interface Avant 1</p>
                </div>
              </div>

              {/* Interface Avant 2 */}
              <div className="relative group flex-shrink-0 transform transition-all duration-500 hover:scale-105">
                <img 
                  src="/references/before-2.png" 
                  alt="Interface avant transformation 2"
                  className="w-auto h-auto object-contain shadow-2xl max-w-[179.71px] max-h-[363.46px] md:max-w-[299.52px] md:max-h-[605.76px]"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* Placeholder */}
                <div className="hidden w-[179.71px] h-[363.46px] md:w-[299.52px] md:h-[605.76px] flex-col items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl">
                  <div className="w-20 h-20 bg-gray-600 rounded-xl mb-4 flex items-center justify-center text-white text-3xl font-bold">
                    2
                  </div>
                  <p className="text-sm text-gray-400">Interface Avant 2</p>
                </div>
              </div>
            </div>

            {/* Interfaces APRÈS avec animation WOW */}
            <div 
              className={`flex justify-center gap-8 md:gap-16 absolute inset-0 transition-all duration-700 ${
                showAfter 
                  ? 'opacity-100 scale-100 blur-0' 
                  : 'opacity-0 scale-110 blur-md pointer-events-none'
              }`}
            >
              {/* Interface Après 1 - Animation depuis la gauche */}
              <div 
                className={`relative group flex-shrink-0 transition-all duration-1000 hover:scale-105 ${
                  showAfter 
                    ? 'translate-x-0 rotate-0' 
                    : '-translate-x-20 -rotate-3'
                }`}
              >
                <img 
                  src="/references/after-1.png" 
                  alt="Interface après transformation 1"
                  className="w-auto h-auto object-contain shadow-2xl max-w-[179.71px] max-h-[363.46px] md:max-w-[299.52px] md:max-h-[605.76px]"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* Placeholder */}
                <div className="hidden w-[179.71px] h-[363.46px] md:w-[299.52px] md:h-[605.76px] flex-col items-center justify-center bg-gradient-to-br from-[#DC1A5F] to-[#32D6E0] rounded-2xl">
                  <div className="w-20 h-20 bg-white rounded-xl mb-4 flex items-center justify-center text-[#DC1A5F] text-3xl font-bold">
                    ✨
                  </div>
                  <p className="text-sm text-white font-semibold">Interface Après 1</p>
                </div>
              </div>

              {/* Interface Après 2 - Animation depuis la droite */}
              <div 
                className={`relative group flex-shrink-0 transition-all duration-1000 hover:scale-105 ${
                  showAfter 
                    ? 'translate-x-0 rotate-0' 
                    : 'translate-x-20 rotate-3'
                }`}
              >
                <img 
                  src="/references/after-2.png" 
                  alt="Interface après transformation 2"
                  className="w-auto h-auto object-contain shadow-2xl max-w-[179.71px] max-h-[363.46px] md:max-w-[299.52px] md:max-h-[605.76px]"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* Placeholder */}
                <div className="hidden w-[179.71px] h-[363.46px] md:w-[299.52px] md:h-[605.76px] flex-col items-center justify-center bg-gradient-to-br from-[#32D6E0] to-[#DC1A5F] rounded-2xl">
                  <div className="w-20 h-20 bg-white rounded-xl mb-4 flex items-center justify-center text-[#32D6E0] text-3xl font-bold">
                    ✨
                  </div>
                  <p className="text-sm text-white font-semibold">Interface Après 2</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;
